// Porte de importar_lote / resolver_duplicata / criar_lead_de_payload
// (supabase/migrations/20260902090400_rpc.sql, mantido no historico do
// git). Mesma ordem de checagem por linha do original: empresa ausente ->
// invalido; supressao -> bloqueado_nao_contatar (nao cria); telefone ou
// empresa+cidade ja existem -> duplicata_suspeita (nao cria, fica pendente
// pra revisao humana); caso contrario -> importado.
import { addDoc, collection, doc, getDoc, getDocs, query, updateDoc, where } from "firebase/firestore";
import { auth, db } from "./firebase";
import { createLead, estaSuprimido } from "./leads";
import { normalizarTelefone, normalizarTexto } from "./normalizar";
import type {
  ImportarLoteResumo,
  ImportItemStatus,
  ImportMatchReason,
  Lead,
  LeadPrioridade,
  ResolverDuplicataAcao,
  ResolverDuplicataResultado,
} from "./types";

function uidAtual(): string | null {
  return auth.currentUser?.uid ?? null;
}

function textoOuNull(v: unknown): string | null {
  const s = typeof v === "string" ? v.trim() : "";
  return s === "" ? null : s;
}

async function criarLeadDePayload(
  payload: Record<string, unknown>,
  origemFonte: string,
  origemDetalhe: string,
  origemColetadaEm: string,
): Promise<Lead> {
  const empresa = textoOuNull(payload.empresa);
  if (!empresa) throw new Error("Campo obrigatório ausente: empresa");

  let uf = textoOuNull(payload.uf)?.toUpperCase() ?? null;
  if (uf && !/^[A-Z]{2}$/.test(uf)) uf = null;

  let email = textoOuNull(payload.email)?.toLowerCase() ?? null;
  if (email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) email = null;

  const prioridade: LeadPrioridade = ["baixa", "media", "alta"].includes(payload.prioridade as string)
    ? (payload.prioridade as LeadPrioridade)
    : "media";

  const tags = Array.isArray(payload.tags) ? (payload.tags as unknown[]).map(String) : [];

  return createLead({
    empresa,
    ramo: textoOuNull(payload.ramo),
    cidade: textoOuNull(payload.cidade),
    uf,
    telefone: textoOuNull(payload.telefone),
    pessoa_contato: textoOuNull(payload.pessoa_contato),
    email,
    site: textoOuNull(payload.site),
    prioridade,
    tags,
    origem_fonte: origemFonte,
    origem_detalhe: origemDetalhe,
    origem_coletada_em: origemColetadaEm,
  });
}

export interface ImportarLoteInput {
  nome: string;
  origem_fonte: string;
  origem_detalhe: string;
  origem_coletada_em: string;
  linhas: Record<string, unknown>[];
  observacao?: string | null;
}

export async function importarLote(input: ImportarLoteInput): Promise<ImportarLoteResumo> {
  if (input.linhas.length === 0) throw new Error("Lote vazio.");
  if (input.linhas.length > 2000) {
    throw new Error(`Lote grande demais (${input.linhas.length} linhas). Máximo de 2000 por chamada.`);
  }
  if (input.origem_coletada_em > new Date().toISOString().slice(0, 10)) {
    throw new Error(`Data de coleta no futuro (${input.origem_coletada_em}).`);
  }

  const batchRef = await addDoc(collection(db, "import_batches"), {
    nome: input.nome,
    origem_fonte: input.origem_fonte,
    origem_detalhe: input.origem_detalhe,
    origem_coletada_em: input.origem_coletada_em,
    observacao: input.observacao ?? null,
    total_linhas: input.linhas.length,
    total_importados: 0,
    total_duplicatas: 0,
    total_bloqueados: 0,
    total_invalidos: 0,
    importado_por: uidAtual(),
    importado_em: new Date().toISOString(),
  });

  const contagens = { importado: 0, duplicata_suspeita: 0, bloqueado_nao_contatar: 0, invalido: 0 };

  let linhaNum = 0;
  for (const linha of input.linhas) {
    linhaNum += 1;

    const empresa = textoOuNull(linha.empresa);
    const cidade = textoOuNull(linha.cidade);
    const telefone = textoOuNull(linha.telefone);
    const itemFonte = textoOuNull(linha.origem_fonte) ?? input.origem_fonte;
    const itemDetalhe = textoOuNull(linha.origem_detalhe) ?? input.origem_detalhe;
    const itemData = textoOuNull(linha.origem_coletada_em as string | undefined) ?? input.origem_coletada_em;

    let status: ImportItemStatus;
    let matchLeadId: string | null = null;
    let motivoMatch: ImportMatchReason | null = null;
    let leadId: string | null = null;
    let erro: string | null = null;

    if (!empresa) {
      status = "invalido";
      erro = "Campo obrigatório ausente: empresa";
    } else if (await estaSuprimido(telefone, empresa, cidade)) {
      status = "bloqueado_nao_contatar";
      motivoMatch = "nao_contatar";
    } else {
      const telNorm = normalizarTelefone(telefone);
      if (telNorm) {
        const r = await getDocs(query(collection(db, "leads"), where("telefone_normalizado", "==", telNorm)));
        if (!r.empty) matchLeadId = r.docs[0].id;
      }
      if (matchLeadId) {
        status = "duplicata_suspeita";
        motivoMatch = "telefone";
      } else {
        const empNorm = normalizarTexto(empresa);
        const cidNorm = normalizarTexto(cidade);
        const r = await getDocs(
          query(collection(db, "leads"), where("empresa_normalizada", "==", empNorm), where("cidade_normalizada", "==", cidNorm)),
        );
        if (!r.empty) {
          matchLeadId = r.docs[0].id;
          status = "duplicata_suspeita";
          motivoMatch = "nome_cidade";
        } else {
          try {
            const lead = await criarLeadDePayload(linha, itemFonte, itemDetalhe, itemData);
            leadId = lead.id;
            status = "importado";
          } catch (e) {
            status = "invalido";
            erro = (e as Error).message.slice(0, 500);
          }
        }
      }
    }

    await addDoc(collection(db, "import_items"), {
      batch_id: batchRef.id,
      linha_num: linhaNum,
      payload: linha,
      status,
      lead_id: leadId,
      match_lead_id: matchLeadId,
      motivo_match: motivoMatch,
      erro,
      origem_fonte: itemFonte,
      origem_detalhe: itemDetalhe,
      origem_coletada_em: itemData,
      resolvido_por: null,
      resolvido_em: null,
      criado_em: new Date().toISOString(),
    });

    contagens[status as keyof typeof contagens] += 1;
  }

  await updateDoc(doc(db, "import_batches", batchRef.id), {
    total_importados: contagens.importado,
    total_duplicatas: contagens.duplicata_suspeita,
    total_bloqueados: contagens.bloqueado_nao_contatar,
    total_invalidos: contagens.invalido,
  });

  return {
    batch_id: batchRef.id,
    nome: input.nome,
    total_linhas: input.linhas.length,
    total_importados: contagens.importado,
    total_duplicatas: contagens.duplicata_suspeita,
    total_bloqueados: contagens.bloqueado_nao_contatar,
    total_invalidos: contagens.invalido,
  };
}

export async function resolverDuplicata(
  itemId: string,
  acao: ResolverDuplicataAcao,
  observacao?: string,
): Promise<ResolverDuplicataResultado> {
  const itemSnap = await getDoc(doc(db, "import_items", itemId));
  if (!itemSnap.exists()) throw new Error(`Item de importação ${itemId} não encontrado.`);
  const item = itemSnap.data() as {
    batch_id: string;
    payload: Record<string, unknown>;
    status: ImportItemStatus;
    match_lead_id: string | null;
    origem_fonte: string | null;
    origem_detalhe: string | null;
    origem_coletada_em: string | null;
    resolvido_em: string | null;
    erro: string | null;
  };

  if (item.resolvido_em) throw new Error(`Item já resolvido em ${item.resolvido_em}.`);
  if (item.status === "bloqueado_nao_contatar" && acao !== "descartar") {
    throw new Error(
      "Item bloqueado pela lista de não contatar: só pode ser descartado " +
        "(para reabrir, revogue a supressão).",
    );
  }

  const loteSnap = await getDoc(doc(db, "import_batches", item.batch_id));
  const lote = loteSnap.data() as { origem_fonte: string; origem_detalhe: string; origem_coletada_em: string };

  let leadId: string | null = null;
  let status: ImportItemStatus;

  if (acao === "criar_novo") {
    const lead = await criarLeadDePayload(
      item.payload,
      item.origem_fonte ?? lote.origem_fonte,
      item.origem_detalhe ?? lote.origem_detalhe,
      item.origem_coletada_em ?? lote.origem_coletada_em,
    );
    leadId = lead.id;
    status = "importado";
  } else if (acao === "mesclar") {
    if (!item.match_lead_id) throw new Error("Item não tem lead correspondente para mesclar.");

    const leadSnap = await getDoc(doc(db, "leads", item.match_lead_id));
    if (!leadSnap.exists()) throw new Error("Lead correspondente não existe mais.");
    const lead = leadSnap.data() as Lead;

    const tagsPayload = Array.isArray(item.payload.tags) ? (item.payload.tags as unknown[]).map(String) : [];
    const tagsMescladas = [...new Set([...lead.tags, ...tagsPayload])];

    // Mescla conservadora: so preenche o que esta vazio, nunca sobrescreve o
    // que ja foi trabalhado pela equipe.
    await updateDoc(doc(db, "leads", item.match_lead_id), {
      ramo: lead.ramo ?? textoOuNull(item.payload.ramo),
      cidade: lead.cidade ?? textoOuNull(item.payload.cidade),
      telefone: lead.telefone ?? textoOuNull(item.payload.telefone),
      pessoa_contato: lead.pessoa_contato ?? textoOuNull(item.payload.pessoa_contato),
      email: lead.email ?? textoOuNull(item.payload.email as string | undefined)?.toLowerCase(),
      site: lead.site ?? textoOuNull(item.payload.site),
      tags: tagsMescladas,
    });

    leadId = item.match_lead_id;
    status = "mesclado";
  } else {
    status = "descartado";
  }

  await updateDoc(doc(db, "import_items", itemId), {
    status,
    lead_id: leadId ?? null,
    erro: observacao ?? item.erro,
    resolvido_por: uidAtual(),
    resolvido_em: new Date().toISOString(),
  });

  return { item_id: itemId, acao, status, lead_id: leadId };
}
